<template>
    <section class="app-settings" v-bind:class="{ 'app-settings--compact': compact }">
        <div class="settings-row">
            <div class="settings-row__label">
                <div class="settings-row__title" v-text="$t('settings.language.title')"/>
            </div>
            <ui-segmented class="settings-row__control" variant="chips" v-model="locale" v-bind:options="languageOptions">
                <template v-slot:icon="{ option }">
                    <country-flag class="settings-flag" v-bind:country="option.value"/>
                </template>
            </ui-segmented>
        </div>

        <div class="settings-row">
            <div class="settings-row__label">
                <div class="settings-row__title" v-text="$t('settings.theme.title')"/>
            </div>
            <ui-segmented class="settings-row__control" variant="tiles" v-model="theme" v-bind:options="themeOptions"/>
        </div>

        <div class="settings-row">
            <div class="settings-row__label">
                <div class="settings-row__title" v-text="$t('settings.currency.title')"/>
                <div class="settings-row__hint" v-text="$t(`settings.currency.${currency}`)"/>
            </div>
            <ui-segmented class="settings-row__control" variant="chips" v-model="currency" v-bind:options="currencyOptions"/>
        </div>

        <div class="settings-row">
            <div class="settings-row__label">
                <div class="settings-row__title" v-text="$t('filter.timeFormat')"/>
                <div class="settings-row__hint" v-text="timeFormatHint"/>
            </div>
            <ui-segmented class="settings-row__control" variant="chips" v-model="timeFormat" v-bind:options="timeFormatOptions"/>
        </div>

        <div class="settings-row">
            <div class="settings-row__label">
                <div class="settings-row__title" v-text="$t('settings.network.title')"/>
                <div class="settings-row__hint">
                    <span class="settings-network-dot" v-bind:class="{ 'settings-network-dot--testnet': isTestnet }"/>
                    {{$t(isTestnet ? 'settings.network.testnet' : 'settings.network.mainnet')}}
                </div>
            </div>
            <a class="settings-network-link" v-bind:href="otherNetworkUrl" rel="noopener">
                {{$t(isTestnet ? 'settings.network.open_mainnet' : 'settings.network.open_testnet')}}
                <icon-arrow class="settings-network-link__icon"/>
            </a>
        </div>
    </section>
</template>

<script>
import IconSun from '@img/icons/lucide/sun.svg?vue';
import IconMoon from '@img/icons/lucide/moon.svg?vue';
import IconSunMoon from '@img/icons/lucide/sun-moon.svg?vue';
import IconArrow from '@img/icons/lucide/arrow-up-right.svg?vue';
import { IS_TESTNET } from '~/config.js';
import { SUPPORTED_LOCALES } from '~/i18n';
import UiSegmented from '~/components/UiSegmented.vue';
import CountryFlag from '~/components/menu/CountryFlag.vue';

const MAINNET_ORIGIN = 'https://tonscan.org';
const TESTNET_ORIGIN = 'https://testnet.tonscan.org';

export default {
    props: {
        // Stacked rows for a narrow dropdown, instead of label-left / control-right:
        compact: Boolean,
    },

    computed: {
        isTestnet() {
            return IS_TESTNET;
        },

        /**
         * Same page on the other network. An address may well be missing there,
         * but the explorer handles that on its own.
         */
        otherNetworkUrl() {
            return (IS_TESTNET ? MAINNET_ORIGIN : TESTNET_ORIGIN) + this.$route.fullPath;
        },

        languageOptions() {
            return SUPPORTED_LOCALES.map(value => ({
                value,
                label: this.$t(`settings.language.${value}`),
            }));
        },

        themeOptions() {
            return [
                { value: 'light', label: this.$t('settings.theme.light'), icon: IconSun },
                { value: 'auto', label: this.$t('settings.theme.auto'), icon: IconSunMoon },
                { value: 'dark', label: this.$t('settings.theme.dark'), icon: IconMoon },
            ];
        },

        currencyOptions() {
            return ['usd', 'rub'].map(value => ({ value, label: value.toUpperCase() }));
        },

        timeFormatOptions() {
            return [
                { value: 'relative', label: this.$t('filter.timeRelativeTitle') },
                { value: 'smart', label: this.$t('filter.timeSmartTitle') },
                { value: 'absolute', label: this.$t('filter.timeAbsoluteTitle') },
            ];
        },

        timeFormatHint() {
            switch (this.timeFormat) {
                case 'relative': return this.$t('filter.timeRelativeDescription');
                case 'smart': return this.$t('filter.timeSmartDescription');
                default: return this.$t('filter.timeAbsoluteDescription');
            }
        },

        locale: {
            get() {
                return this.$store.state.appLocale;
            },
            set(locale) {
                this.$store.commit('updateLocale', { locale });
            },
        },

        theme: {
            get() {
                return this.$store.state.appTheme;
            },
            set(theme) {
                this.$store.commit('updateTheme', theme);
            },
        },

        currency: {
            get() {
                return this.$store.state.exchangeRateCurrency;
            },
            set(currency) {
                this.$store.commit('updateExchangeRateCurrency', currency);
            },
        },

        timeFormat: {
            get() {
                // Unix time is a sub-mode of the absolute format, picked in the tx filter:
                return this.$store.state.timeFormatData === 'unix' ? 'absolute' : this.$store.state.timeFormatData;
            },
            set(format) {
                this.$store.commit('updateTimeFormatData', format);
            },
        },
    },

    components: {
        UiSegmented,
        CountryFlag,
        IconArrow,
    },
};
</script>

<style lang="scss">
.app-settings {
    padding: 4px 8px 8px;
}

.settings-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 14px 12px;
    border-bottom: 1px solid var(--card-row-separator);

    &:last-child {
        border-bottom: none;
    }

    &__label {
        min-width: 0;
    }

    &__title {
        font-size: 14px;
        font-weight: 500;
        letter-spacing: -0.01em;
        color: var(--body-text-color);
    }

    &__hint {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 3px;
        font-size: 12.5px;
        color: var(--body-muted-text-color);
    }

    &__control {
        flex-shrink: 0;
    }
}

.settings-flag {
    display: inline-flex;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    svg {
        width: 100%;
        height: 100%;
    }
}

.settings-network-dot {
    display: inline-block;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--green-bright);
    box-shadow: 0 0 0 3px var(--badge-green-background-color);

    &--testnet {
        background: var(--orange-bright);
        box-shadow: 0 0 0 3px var(--badge-orange-background-color);
    }
}

.settings-network-link {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 36px;
    padding: 0 12px 0 14px;
    border-radius: 10px;
    background: var(--body-light-muted-color);
    color: var(--body-text-color);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: -0.01em;
    white-space: nowrap;
    text-decoration: none !important;
    transition: background-color .15s ease, color .15s ease, transform .1s ease;

    &:hover {
        background: var(--icon-button-hover-background);
        color: var(--blue-bright);
    }
    &:active {
        transform: scale(.97);
    }

    &__icon {
        width: 15px;
        height: 15px;
        opacity: .7;
    }
}

// Narrow layouts (the dropdown, or a phone-wide viewport): label above, control full width.
.app-settings--compact {
    padding: 2px 6px 6px;

    .settings-row {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
        padding: 12px 10px;

        // Title on the left, the chosen value on the right, one line:
        &__label {
            display: flex;
            align-items: baseline;
            justify-content: space-between;
            gap: 12px;
        }
        &__hint {
            margin-top: 0;
            white-space: nowrap;
        }

        &__control {
            display: flex;
            .ui-segmented__option {
                flex: 1;
            }
        }
    }
    .settings-network-link {
        justify-content: center;
    }
}

@media all and (max-width: 560px) {
    .settings-row {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;

        &__control {
            display: flex;
            .ui-segmented__option {
                flex: 1;
            }
        }
    }
    .settings-network-link {
        justify-content: center;
    }
}
</style>
